import { c as _c } from "react/compiler-runtime";
// Кнопка «?» в хроме админки. Открывает поповер «Что это за страница» с кратким
// описанием и кнопкой «Пройти тур», запускающей пошаговый тур текущей страницы.
// На страницах без тура показывает только общую подсказку. Ненавязчиво
// сигналит точкой, если тур ещё не проходили.
//
// Поповер уходит порталом в body и позиционируется fixed с зажимом к вьюпорту.
// Кнопка живёт в sticky-сайдбаре с overflow-y-auto: сайдбар — отдельный слой,
// и даже fixed-поповер внутри него обрезался по его правому краю, а поверх
// ложился скроллбар сайдбара. Портал выводит поповер из этого слоя.
import { Button } from "@osmix/ui/button";
import { placeHelpPopover } from "@osmix/ui-primitives/tour-layout";
import { HelpCircle, Play } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { useTour } from "./tour-provider";
import { toursForPath } from "./tours";
const WIDTH = 288; // w-72
const MARGIN = 12; // минимальный зазор от краёв экрана

export function HelpButton(t0) {
  const $ = _c(33);
  const {
    className
  } = t0;
  const {
    t
  } = useTranslation();
  const location = useLocation();
  const {
    start,
    isSeen
  } = useTour();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const btnRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      top: 0,
      left: 0,
      width: WIDTH
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [pos, setPos] = useState(t1);
  const [tour, ...sectionTours] = toursForPath(location.pathname);
  const unseen = [tour, ...sectionTours].some(item => item && !isSeen(item.key));
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) {
        return;
      }
      const vw = window.innerWidth;
      const width = Math.min(WIDTH, vw - MARGIN * 2);
      const height = popoverRef.current?.getBoundingClientRect().height ?? 240;
      const {
        top,
        left
      } = placeHelpPopover(b, {
        width,
        height
      }, {
        width: vw,
        height: window.innerHeight
      }, MARGIN);
      setPos({
        top,
        left,
        width
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const reposition = t2;
  let t3;
  let t4;
  if ($[2] !== open) {
    t3 = () => {
      if (open) {
        reposition();
      }
    };
    t4 = [open, reposition];
    $[2] = open;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setOpen(false);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== location.pathname) {
    t6 = [location.pathname];
    $[6] = location.pathname;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== open) {
    t7 = () => {
      if (!open) {
        return;
      }
      const onDown = event => {
        const target = event.target as Node;
        if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) {
          return;
        }
        setOpen(false);
      };
      const onKey = event_0 => {
        if (event_0.key === "Escape") {
          setOpen(false);
        }
      };
      const onMove = () => reposition();
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
      window.addEventListener("resize", onMove);
      window.addEventListener("scroll", onMove, true);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("keydown", onKey);
        window.removeEventListener("resize", onMove);
        window.removeEventListener("scroll", onMove, true);
      };
    };
    t8 = [open, reposition];
    $[8] = open;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] !== start) {
    t9 = key => {
      setOpen(false);
      start(key);
    };
    $[11] = start;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const runTour = t9;
  let t10;
  if ($[13] !== className) {
    t10 = cn("relative", className);
    $[13] = className;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t) {
    t11 = t("tour.help");
    $[15] = t;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t) {
    t12 = t("tour.help");
    $[17] = t;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setOpen(_temp);
    t14 = <HelpCircle className="size-4" />;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  let t15;
  if ($[21] !== open || $[22] !== t11 || $[23] !== t12) {
    t15 = <Button ref={btnRef} variant="ghost" size="icon-sm" aria-label={t11} title={t12} aria-haspopup="dialog" aria-expanded={open} onClick={t13}>{t14}</Button>;
    $[21] = open;
    $[22] = t11;
    $[23] = t12;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== open || $[26] !== unseen) {
    t16 = unseen && !open && <span aria-hidden={true} className="absolute right-1 top-1 size-2 animate-pulse rounded-full bg-primary ring-2 ring-background" />;
    $[25] = open;
    $[26] = unseen;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = open && createPortal(<div ref={popoverRef} role="dialog" aria-label={t("tour.help")} className="fixed z-[120] max-h-[calc(100vh-1.5rem)] overflow-auto rounded-xl border bg-popover p-4 text-popover-foreground shadow-xl" style={{
    top: pos.top,
    left: pos.left,
    width: pos.width
  }}><p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("tour.whatIsThis")}</p><h3 className="text-sm font-semibold">{tour ? t(tour.titleKey) : t("tour.help")}</h3><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{tour ? t(tour.descKey) : t("tour.noTour")}</p>{tour && <Button size="sm" className="mt-3 w-full" onClick={() => runTour(tour.key)}><Play className="size-4" />{t("tour.startTour")}</Button>}{sectionTours.length > 0 && <div className="mt-3 border-t pt-3"><p className="mb-1.5 text-xs text-muted-foreground">{t("tour.sectionTours")}</p><div className="flex flex-col gap-1.5">{sectionTours.map(item_0 => <Button key={item_0.key} size="sm" variant="outline" className="w-full justify-start" onClick={() => runTour(item_0.key)}><Play className="size-3.5" />{t(item_0.titleKey)}{!isSeen(item_0.key) && <span aria-hidden={true} className="ml-auto size-1.5 rounded-full bg-primary" />}</Button>)}</div></div>}</div>, document.body);
  let t18;
  if ($[28] !== t10 || $[29] !== t15 || $[30] !== t16 || $[31] !== t17) {
    t18 = <div ref={rootRef} className={t10}>{t15}{t16}{t17}</div>;
    $[28] = t10;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  return t18;
}
function _temp(value) {
  return !value;
}